import React, {useRef, useEffect, useState, KeyboardEvent} from 'react';
import {useFocusRing} from 'react-aria';

interface NumberSegmentProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isDisabled?: boolean;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    allowNegative?: boolean;
    allowDecimal?: boolean;
    type?: 'integer' | 'decimal' | 'literal';
    onFocus?: () => void;
    onBlur?: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    autoFocus?: boolean;
    'aria-label'?: string;
}

export function NumberSegment(props: NumberSegmentProps) {
    const {
        value,
        onChange,
        placeholder = '0',
        isDisabled = false,
        maxLength,
        minValue,
        maxValue,
        allowNegative = true,
        allowDecimal = false,
        type = 'integer',
        onFocus,
        onBlur,
        onNext,
        onPrevious,
        autoFocus = false,
        'aria-label': ariaLabel,
    } = props;

    const ref = useRef<HTMLSpanElement>(null);
    const [isEditing, setIsEditing] = useState(false);
    const {isFocusVisible, focusProps} = useFocusRing();

    useEffect(() => {
        if (autoFocus && ref.current) {
            ref.current.focus();
        }
    }, [autoFocus]);

    const handleClick = () => {
        if (!isDisabled && ref.current) {
            ref.current.focus();
            // Select all text on click
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(ref.current);
            selection?.removeAllRanges();
            selection?.addRange(range);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
        if (isDisabled) return;

        // Arrow navigation
        if (e.key === 'ArrowRight' || e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            onNext?.();
        } else if (e.key === 'ArrowLeft' || e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            onPrevious?.();
        }
        // Increment/decrement
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const current = parseInt(value) || 0;
            const newValue = current + 1;
            if (maxValue === undefined || newValue <= maxValue) {
                onChange(newValue.toString());
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            const current = parseInt(value) || 0;
            const newValue = current - 1;
            if (minValue === undefined || newValue >= minValue) {
                onChange(newValue.toString());
            }
        }
        // Clear on Delete/Backspace
        else if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            onChange('');
        }
    };

    const handleInput = (e: React.FormEvent<HTMLSpanElement>) => {
        const text = e.currentTarget.textContent || '';

        // Validate input based on type
        let validText = text;

        if (type === 'integer') {
            // Only allow digits and optionally minus sign
            validText = text.replace(/[^\d-]/g, '');
            if (!allowNegative) {
                validText = validText.replace(/-/g, '');
            }
        } else if (type === 'decimal') {
            // Allow digits, decimal point, and optionally minus
            validText = text.replace(/[^\d.,-]/g, '');
            if (!allowNegative) {
                validText = validText.replace(/-/g, '');
            }
        }

        // Apply max length
        if (maxLength && validText.length > maxLength) {
            validText = validText.slice(0, maxLength);
        }

        // Update content if it changed
        if (validText !== text && ref.current) {
            ref.current.textContent = validText;
        }

        onChange(validText);
    };

    const handleFocus = () => {
        setIsEditing(true);
        onFocus?.();
    };

    const handleBlur = () => {
        setIsEditing(false);
        onBlur?.();
    };

    const isPlaceholder = !value || value === '';
    const displayValue = isPlaceholder ? placeholder : value;

    // Literal segments (like decimal point, currency symbol) are not editable
    if (type === 'literal') {
        return (
            <span className="segment px-1 text-gray-900">
                {value}
            </span>
        );
    }

    return (
        <span
            ref={ref}
            role="spinbutton"
            aria-label={ariaLabel}
            aria-valuenow={value ? parseInt(value) : undefined}
            aria-valuemin={minValue}
            aria-valuemax={maxValue}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
            contentEditable={!isDisabled}
            suppressContentEditableWarning
            {...focusProps}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`
                segment
                px-1
                text-right
                rounded-sm
                inline-block
                ${isFocusVisible ? 'bg-pink-100 text-pink-900' : ''}
                ${isPlaceholder ? 'text-gray-400' : 'text-gray-900'}
                ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-text'}
            `}
        >
            {displayValue}
        </span>
    );
}